import { create } from 'zustand';

import { clientConfig } from '@/config/clientConfig';

import { getProfileEncryptionTypeById, getProfileType } from '../api/helpers';
import { ApiProfileResponse, ProfileType } from '../api/types';
import { EncryptionType } from '../lib/secure-json/core/crypto-core/types';

export type ProfileStoreState = {
  profileType: ProfileType | null;
  encryptionType: EncryptionType | null;
  publicKey: string | null;
  isBeneficiary: boolean | null;
  isAssistant: boolean | null;
  mainUserId: number | null;
  baseCurrency: string | null;
  soweAppendixSuppressCancelConfirmation: boolean;
  ocrSuppressLetsTryConfirmation: boolean;
  hasCompletedOnboarding: boolean;
  firstName?: string;
  lastName?: string;
  profileId: number | null;
};

export type ProfileStore = ProfileStoreState & {
  setProfileType: (type: ProfileType) => void;
  setMainUserId: (id: number) => void;
  setEncryptionType: (encryptionType: EncryptionType) => void;
  setPublicKey: (publicKey: string | null) => void;
  setBaseCurrency: (baseCurrency: string) => void;
  setSoweAppendixSuppressCancelConfirmation: (suppress: boolean) => void;
  setOcrSuppressLetsTryConfirmation: (suppress: boolean) => void;
  setHasCompletedOnboarding: (hasCompletedOnboarding: boolean) => void;
  setFullName: (firstName?: string, lastName?: string) => void;
  setProfileId: (id: number) => void;
  resolveBaseCurrency: () => string; // TODO move out to helper/etc
  clear: () => void;
};

export const useProfileStore = create<ProfileStore>((set, get) => ({
  profileType: null,
  publicKey: null,
  encryptionType: null,
  isBeneficiary: null,
  isAssistant: null,
  mainUserId: null,
  baseCurrency: null,
  soweAppendixSuppressCancelConfirmation: false,
  ocrSuppressLetsTryConfirmation: false,
  hasCompletedOnboarding: false,
  profileId: null,

  setProfileType: (profileType) =>
    set({
      profileType,
      isBeneficiary: profileType === ProfileType.Beneficiary,
      isAssistant: profileType === ProfileType.Assistant,
    }),

  setMainUserId: (id) => set({ mainUserId: id }),
  setProfileId: (id) => set({ profileId: id }),
  setEncryptionType: (encryptionType) => set({ encryptionType }),
  setPublicKey: (publicKey) => set({ publicKey }),
  setBaseCurrency: (baseCurrency) => set({ baseCurrency }),
  setSoweAppendixSuppressCancelConfirmation: (soweAppendixSuppressCancelConfirmation) =>
    set({ soweAppendixSuppressCancelConfirmation }),
  setOcrSuppressLetsTryConfirmation: (ocrSuppressLetsTryConfirmation) =>
    set({ ocrSuppressLetsTryConfirmation }),
  setHasCompletedOnboarding: (hasCompletedOnboarding) => set({ hasCompletedOnboarding }),
  setFullName: (firstName, lastName) => set({ firstName, lastName }),
  clear: () => set({ publicKey: null }),
  resolveBaseCurrency: () => get().baseCurrency ?? clientConfig.defaultProfileBaseCurrency,
}));

export const getProfileStateProps = (
  profileData: ApiProfileResponse,
): Partial<ProfileStoreState> => {
  const profileProps: Partial<ProfileStoreState> = {
    profileType: getProfileType(profileData.type),
    encryptionType: profileData.encryptionTypeId
      ? getProfileEncryptionTypeById(profileData.encryptionTypeId)
      : clientConfig.defaultEncryption,
    publicKey: profileData.publicKey ?? undefined,
    mainUserId: profileData.mainUserId ?? undefined,
    baseCurrency: profileData.baseCurrency?.title ?? clientConfig.defaultProfileBaseCurrency,
    firstName: profileData.firstName ?? undefined,
    lastName: profileData.lastName ?? undefined,
    soweAppendixSuppressCancelConfirmation: profileData.soweAppendixSuppressCancelConfirmation,
    ocrSuppressLetsTryConfirmation: profileData.ocrSuppressLetsTryConfirmation,
    profileId: profileData.id,
  };

  return profileProps;
};
